import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import type { RootState } from "_shared/_stores/root-reducer";
import Guid from "_shared/guids/guid";
import type { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { userNetworkConnectionsApi } from "users/networking/connections/_api/user-network-connection-api";
import type NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";

export type NetworkConnectionRemoval = {
  userId?: string;
  connectionId?: string;
};

/**
 * A cache of the network connections that this session has loaded, by id, plus the pages loaded
 * for each search. The server pages the list, so this slice never holds the full list: a person
 * who is not here may still be a connection. `statusByUserId` holds what the session knows about
 * one person: true (connected), false (not connected), or no entry (not asked yet). Ask with
 * `useNetworkConnectionStatus`.
 */
export type NetworkConnectionsStoreState = {
  byQuery: {
    entries: Record<string, Paginated<UserInformationJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
    /**
     * How many of the server's rows the held pages have read past, less the rows removed from them
     * since. The next page starts there, so a removal does not make it skip the person who moved up.
     */
    offsets: Record<string, number>;
    /** Counts the connections that arrived and are on no held page. Only a page-zero refresh can place them. */
    stale: Record<string, number>;
  };
  byUserId: {
    entries: Record<string, UserInformationJSON>;
  };
  byConnectionId: {
    entries: Record<string, UserInformationJSON>;
  };
  statusByUserId: {
    entries: Record<string, boolean>;
  };
  /** Ids that a bulk lookup did not return, and why. A later load or upsert clears the entry. */
  unavailableByUserId: {
    entries: Record<string, UnavailableReason>;
  };
};

const initialState: NetworkConnectionsStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
    offsets: {},
    stale: {},
  },
  byUserId: {
    entries: {},
  },
  byConnectionId: {
    entries: {},
  },
  statusByUserId: {
    entries: {},
  },
  unavailableByUserId: {
    entries: {},
  },
};

function upsertNetworkConnectionJSON(state: NetworkConnectionsStoreState, connection: UserInformationJSON): void {
  state.byUserId.entries[connection.id] = connection;
  state.statusByUserId.entries[connection.id] = true;
  delete state.unavailableByUserId.entries[connection.id];
  if (connection.connectionId) {
    state.byConnectionId.entries[connection.connectionId] = connection;
  }
}

/**
 * Replaces the connection on each loaded page that shows it. It adds it to no page: the server
 * decides where it belongs, so a search that does not show it is marked stale for a refresh.
 */
function replaceOnLoadedPages(state: NetworkConnectionsStoreState, connection: UserInformationJSON): void {
  for (const [cacheKey, page] of Object.entries(state.byQuery.entries)) {
    const index = page.data.findIndex((item) => item.id === connection.id);
    if (index >= 0) {
      page.data[index] = connection;
    } else {
      state.byQuery.stale[cacheKey] = (state.byQuery.stale[cacheKey] ?? 0) + 1;
    }
  }
}

function removeNetworkConnectionJSON(state: NetworkConnectionsStoreState, removal: NetworkConnectionRemoval): void {
  const connectionByConnectionId = removal.connectionId
    ? state.byConnectionId.entries[removal.connectionId]
    : undefined;
  const connectionByUserId = removal.userId ? state.byUserId.entries[removal.userId] : undefined;
  const userId = removal.userId ?? connectionByConnectionId?.id;
  const connectionId = removal.connectionId ?? connectionByUserId?.connectionId ?? undefined;

  if (userId) {
    delete state.byUserId.entries[userId];
    state.statusByUserId.entries[userId] = false;
  }
  if (connectionId) {
    delete state.byConnectionId.entries[connectionId];
  }

  for (const [cacheKey, page] of Object.entries(state.byQuery.entries)) {
    const remaining = page.data.filter((item) => item.id !== userId && (!connectionId || item.connectionId !== connectionId));
    if (remaining.length === page.data.length) continue;
    page.data = remaining;
    // The rows after it each move up one place on the server. Keep the server's page count and
    // next-page flag: recomputing them here would end the list before the person who moved up.
    page.headers.totalElements = Math.max(0, page.headers.totalElements - 1);
    state.byQuery.offsets[cacheKey] = Math.max(0, (state.byQuery.offsets[cacheKey] ?? 0) - 1);
  }
}

/** Adds the next page after the rows held, and replaces a connection that is on both. */
function appendNetworkConnectionPage(
  current: Paginated<UserInformationJSON> | undefined,
  next: Paginated<UserInformationJSON>,
): Paginated<UserInformationJSON> {
  if (!current) return next;

  const merged = [...current.data];
  const indexesByUserId = new Map(merged.map((connection, index) => [connection.id, index]));
  for (const connection of next.data) {
    const existingIndex = indexesByUserId.get(connection.id);
    if (existingIndex === undefined) {
      indexesByUserId.set(connection.id, merged.length);
      merged.push(connection);
    } else {
      merged[existingIndex] = connection;
    }
  }

  return { data: merged, headers: next.headers };
}

/**
 * Puts page zero in front of the rows held. A row that a new connection pushed off page zero
 * follows it, still in order. When page zero is the whole list, it replaces what is held. The
 * headers of the furthest page held still say whether there is a next page.
 */
function refreshNetworkConnectionPages(
  current: Paginated<UserInformationJSON> | undefined,
  first: Paginated<UserInformationJSON>,
): Paginated<UserInformationJSON> {
  const isWholeList = !(first.headers.hasNext ?? first.headers.pageIndex < first.headers.totalPages - 1);
  if (!current || isWholeList) return first;

  const firstIds = new Set(first.data.map((connection) => connection.id));
  return {
    data: [...first.data, ...current.data.filter((connection) => !firstIds.has(connection.id))],
    headers: { ...current.headers, totalElements: first.headers.totalElements, totalPages: first.headers.totalPages },
  };
}

export const networkConnectionsSlice = createSlice({
  name: "networkConnections",
  initialState,
  reducers: {
    upsertNetworkConnection: (state, action: PayloadAction<UserInformationJSON>) => {
      upsertNetworkConnectionJSON(state, action.payload);
      replaceOnLoadedPages(state, action.payload);
    },
    removeNetworkConnection: (state, action: PayloadAction<NetworkConnectionRemoval>) => {
      removeNetworkConnectionJSON(state, action.payload);
    },
    /**
     * Drops what the session knows about one person's status, so `useNetworkConnectionStatus`
     * asks the server again. For a change that names the person but carries too little to store
     * the connection: the bulk status request holds no subscription, so invalidating its tags
     * refetches nothing.
     */
    forgetNetworkConnectionStatus: (state, action: PayloadAction<string>) => {
      delete state.statusByUserId.entries[action.payload];
      delete state.unavailableByUserId.entries[action.payload];
    },
    clearNetworkConnections: () => initialState,
  },
  extraReducers: (builder) => {
    // Only a request that says how to keep its page is a list's page. Any other one only indexes
    // the connections it read.
    builder.addMatcher(userNetworkConnectionsApi.endpoints.getUserNetworkConnections.matchPending, (state, action) => {
      const { query, pages } = action.meta.arg.originalArgs;
      if (!pages) return;
      state.byQuery.loading[query.cacheKey] = true;
      state.byQuery.error[query.cacheKey] = null;
    });

    builder.addMatcher(userNetworkConnectionsApi.endpoints.getUserNetworkConnections.matchFulfilled, (state, action) => {
      const { query, pages } = action.meta.arg.originalArgs;
      for (const connection of action.payload.data) {
        upsertNetworkConnectionJSON(state, connection);
      }
      if (!pages) return;

      const cacheKey = query.cacheKey;
      const current = state.byQuery.entries[cacheKey];
      const kept = pages === "next"
        ? appendNetworkConnectionPage(current, action.payload)
        : refreshNetworkConnectionPages(current, action.payload);
      const { pageIndex, pageSize } = query.paginationParameters;
      const readTo = (pageIndex + 1) * pageSize;
      // A page that replaced what was held starts the count again.
      state.byQuery.offsets[cacheKey] = kept === action.payload
        ? readTo
        : Math.max(state.byQuery.offsets[cacheKey] ?? 0, readTo);
      state.byQuery.entries[cacheKey] = kept;
      state.byQuery.loading[cacheKey] = false;
    });

    builder.addMatcher(userNetworkConnectionsApi.endpoints.getUserNetworkConnections.matchRejected, (state, action) => {
      const { query, pages } = action.meta.arg.originalArgs;
      // RTK Query rejects a request it did not send, because the same one is in flight. That one
      // is still loading, and will answer for both.
      if (!pages || action.meta.condition) return;
      state.byQuery.loading[query.cacheKey] = false;
      state.byQuery.error[query.cacheKey] = action.error;
    });

    builder.addMatcher(
      userNetworkConnectionsApi.endpoints.getUserNetworkConnectionsByUserIds.matchFulfilled,
      (state, action) => {
        for (const connection of action.payload.found) {
          upsertNetworkConnectionJSON(state, connection);
        }
        for (const unavailable of action.payload.unavailable) {
          if (unavailable.reason === "missing") {
            removeNetworkConnectionJSON(state, { userId: unavailable.id });
          }
          state.unavailableByUserId.entries[unavailable.id] = unavailable.reason;
        }
      },
    );

    builder.addMatcher(userNetworkConnectionsApi.endpoints.removeNetworkConnection.matchFulfilled, (state, action) => {
      removeNetworkConnectionJSON(state, {
        connectionId: action.meta.arg.originalArgs.connectionId.value,
      });
    });
  },
});

export const {
  clearNetworkConnections,
  forgetNetworkConnectionStatus,
  removeNetworkConnection,
  upsertNetworkConnection,
} = networkConnectionsSlice.actions;

const selectNetworkConnectionsByQueryKey = createSelector(
  [(state: RootState) => state.networkConnections.byQuery.entries, (_: RootState, cacheKey: string) => cacheKey],
  (entries, cacheKey) => {
    const page = entries[cacheKey];
    if (!page) return PaginatedResponse.empty<UserInformation>();
    return PaginatedResponse.from(UserInformation.fromJSONArray(page.data), page.headers);
  },
);

const selectNetworkConnectionByUserId = createSelector(
  [(state: RootState) => state.networkConnections.byUserId.entries, (_: RootState, userId?: Guid) => userId?.value],
  (entries, userIdValue) => {
    if (!userIdValue || !entries[userIdValue]) return undefined;
    return UserInformation.fromJSON(entries[userIdValue]);
  },
);

const selectNetworkConnectionByConnectionId = createSelector(
  [
    (state: RootState) => state.networkConnections.byConnectionId.entries,
    (_: RootState, connectionId?: Guid) => connectionId?.value,
  ],
  (entries, connectionIdValue) => {
    if (!connectionIdValue || !entries[connectionIdValue]) return undefined;
    return UserInformation.fromJSON(entries[connectionIdValue]);
  },
);

/** The pages loaded so far for this search, merged in server order. */
export const getNetworkConnectionsByQuery = (query: NetworkConnectionQuery) => (state: RootState) =>
  selectNetworkConnectionsByQueryKey(state, query.cacheKey);

export const getIsLoadingNetworkConnectionsByQuery = (query: NetworkConnectionQuery) => (state: RootState) =>
  state.networkConnections.byQuery.loading[query.cacheKey] ?? false;

export const getErrorLoadingNetworkConnectionsByQuery = (query: NetworkConnectionQuery) => (state: RootState) =>
  state.networkConnections.byQuery.error[query.cacheKey] ?? null;

/** Where on the server the next page of this search starts. */
export const getNextNetworkConnectionOffsetByQuery = (query: NetworkConnectionQuery) => (state: RootState) =>
  state.networkConnections.byQuery.offsets[query.cacheKey] ?? 0;

/** Changes when a connection arrives that no held page of this search shows. */
export const getStaleNetworkConnectionsByQuery = (query: NetworkConnectionQuery) => (state: RootState) =>
  state.networkConnections.byQuery.stale[query.cacheKey] ?? 0;

/** True: connected. False: not connected. Undefined: not asked yet. */
export const getNetworkConnectionStatusByUserId = (userId?: Guid) => (state: RootState): boolean | undefined =>
  userId ? state.networkConnections.statusByUserId.entries[userId.value] : undefined;

/** Why a bulk lookup did not return this person, if it did not. */
export const getNetworkConnectionUnavailableReasonByUserId = (userId?: Guid) => (state: RootState): UnavailableReason | undefined =>
  userId ? state.networkConnections.unavailableByUserId.entries[userId.value] : undefined;

/** A connection this session has loaded. Undefined does not mean "not connected". */
export const getNetworkConnectionByUserId = (userId?: Guid) => (state: RootState) =>
  selectNetworkConnectionByUserId(state, userId);

export const getNetworkConnectionByConnectionId = (connectionId?: Guid) => (state: RootState) =>
  selectNetworkConnectionByConnectionId(state, connectionId);
